import React, { useState } from 'react';
import request from 'utils/request_ajax';
import { UniversalUploader } from 'components/shared/UniversalUploader';
import { useFeatureFlags } from 'components/shared/hooks';
import { Spinner } from 'components/ui';
import StatusLabel from '../../shared/StatusLabel';
import RemoveImageModal from './RemoveImageModal';

const Loading = ({ loading, children }) => {
  if (loading) {
    return (
      <div className="d-f h-100 jc-c ai-c">
        <Spinner />
      </div>
    );
  }
  return children;
};

const SettingsImageUploader = ({
  url,
  title,
  type,
  info,
  imageUrl,
  accept,
  currentCompany,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [universalUploaderModalVisible, setUniversalUploaderModalVisible] =
    useState(false);
  const [removeFileModalVisible, setRemoveFileModalVisible] = useState(false);
  const hasFileUploadFF = useFeatureFlags(
    'learning-file-stack-upload-replacement',
  );

  const closeRemoveModal = () => {
    setRemoveFileModalVisible(false);
  };

  const onFileStackFileChange = event => {
    const { files, name } = event.target;
    const file = files[0];
    if (!file) {
      return;
    }
    setError(false);
    setLoading(true);
    const formdata = new FormData();
    formdata.append(name, file);
    request
      .patch(url, formdata, {
        contentType: false,
        processData: false,
      })
      .then(() => {
        // refresh the page to show the updated image in the browser
        window.location.reload();
      })
      .catch(err => {
        console.error(err);
        setError(true);
        setLoading(false);
      });
  };

  const saveBssUpload = newUpload => {
    return new Promise(async (resolve, reject) => {
      setError(false);
      setLoading(true);
      const { bssUploadId } = newUpload;
      const params = {
        company: {
          [`new_${type}_bss_id`]: bssUploadId,
        },
      };
      request
        .patchJSON(url, params)
        .then(() => {
          window.location.reload();
          setUniversalUploaderModalVisible(false);
          resolve();
        })
        .catch(e => {
          setUniversalUploaderModalVisible(false);
          setError(true);
          setLoading(false);
          reject(new Error(e));
        });
    });
  };

  const onFileRemove = () => {
    setError(false);
    setLoading(true);
    setRemoveFileModalVisible(false);
    request
      .patch(`/settings/remove_${type}`)
      .then(() => {
        window.location.reload();
      })
      .catch(() => {
        setError(true);
        setLoading(false);
      });
  };

  const fileUploader = () => {
    const browseText = t(
      'settings.tab.customization.upload_image.browse_button',
    );
    if (hasFileUploadFF) {
      return (
        <button
          type="button"
          className="NewButton Button--customColor"
          onClick={() => setUniversalUploaderModalVisible(true)}
        >
          {browseText}
        </button>
      );
    }
    return (
      <>
        <label
          className="Button--customColor NewButton"
          htmlFor={`company_${type}`}
        >
          {browseText}
        </label>
        <input
          className="d-n"
          id={`company_${type}`}
          onChange={onFileStackFileChange}
          accept={accept}
          tabIndex="0"
          type="file"
          name={`company[new_${type}]`}
        />
      </>
    );
  };

  return (
    <div className={`controls ImageUploader ImageUploader-${type}`}>
      <div>
        <div className="d-f ai-c">
          <span>{title}</span>
          <StatusLabel error={error} />
        </div>
        <span className="ImageUploader-info">{info}</span>
      </div>
      <div className="ImageUploader-current">
        <Loading loading={loading}>
          {imageUrl && <img alt={`company ${type}`} src={imageUrl} />}
        </Loading>
      </div>
      {!loading && (
        <div className="d-f">
          <div className="ImageUploader-fileInput">{fileUploader()}</div>
          {imageUrl && (
            <button
              type="button"
              className="NewButton NewButton--gray ml-10"
              onClick={() => setRemoveFileModalVisible(true)}
            >
              {t('settings.tab.customization.upload_image.remove_button')}
            </button>
          )}
        </div>
      )}
      {universalUploaderModalVisible && (
        <UniversalUploader
          accept={accept}
          displayInModal
          modalShown={universalUploaderModalVisible}
          onModalDismiss={() => setUniversalUploaderModalVisible(false)}
          onComplete={saveBssUpload}
          deleteUploadRecord={false}
        />
      )}
      {removeFileModalVisible && (
        <RemoveImageModal
          shown={removeFileModalVisible}
          type={type}
          onDimss={closeRemoveModal}
          currentCompany={currentCompany}
          onFileRemove={onFileRemove}
        />
      )}
    </div>
  );
};

export default SettingsImageUploader;
